// Event detail permalink at /events/{bookingId}. Canonical shareable URL for
// a single event. Reads from the same config/publicEvents doc as the list
// page — no extra query, just filter by bookingId. For past events with
// linked shifts, shows the full pancake gallery from that day.

import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import SiteNav from "./components/SiteNav";
import SiteFooter from "./sections/SiteFooter";
import PanBar from "./components/PanBar";
import { gcalUrl, shareEvent, downloadIcs } from "./EventsPage";

const BG = "#081a2a";

// confirmedDateMs is UTC midnight — use UTC accessors to get the right
// calendar day, then format as a local Date for display.
function eventDay(ms) {
  if (!ms) return null;
  const d = new Date(ms);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}
function formatDate(ms) {
  const d = eventDay(ms);
  if (!d) return null;
  return d.toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}
function formatTime(ms) {
  if (!ms) return null;
  return new Date(ms).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function mapsUrl(address) {
  if (!address) return null;
  return `https://maps.google.com/?q=${encodeURIComponent(address)}`;
}

export default function EventDetail({ bookingId }) {
  const [ev, setEv] = useState(undefined); // undefined = loading, null = not found
  const [liveShift, setLiveShift] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = "Event · The Joy of Pancakes";
    return () => { document.title = "The Joy of Pancakes"; };
  }, []);

  useEffect(() => {
    return onSnapshot(
      doc(db, "config", "publicEvents"),
      (snap) => {
        if (!snap.exists()) { setEv(null); return; }
        const items = snap.data()?.items || [];
        const match = items.find(e => e.bookingId === bookingId);
        setEv(match || null);
        if (match) {
          document.title = `${match.publicTitle || match.eventName || "Event"} · The Joy of Pancakes`;
        }
      },
      () => setEv(null),
    );
  }, [bookingId]);

  useEffect(() => {
    return onSnapshot(
      doc(db, "config", "liveShift"),
      (snap) => setLiveShift(snap.exists() ? snap.data() : null),
      () => setLiveShift(null),
    );
  }, []);

  const isLive = liveShift?.isLive && liveShift?.bookingId === bookingId;
  const address = ev?.venueAddress || ev?.publicLocation;
  const maps = mapsUrl(address);
  const gcal = ev ? gcalUrl(ev) : null;
  const photos = ev?.pastStats?.samplePhotoUrls || [];
  const count = ev?.pastStats?.orderCount ?? 0;
  const isPast = ev?.confirmedDateMs && ev.confirmedDateMs < Date.now() - 86400000;

  return (
    <div style={{ background: BG, minHeight: "100vh", color: "var(--jop-cream)", fontFamily: "var(--jop-display)" }}>
      <SiteNav />

      <main style={{ maxWidth: "680px", margin: "0 auto", padding: "5.5rem 1.25rem 4rem" }}>
        {/* Back to all events */}
        <div style={{ marginBottom: "1.5rem" }}>
          <a href="/events" style={{
            color: "rgba(255,218,173,0.6)", fontSize: "0.85rem", fontWeight: 600,
            textDecoration: "none",
          }}>
            ← All events
          </a>
        </div>

        {/* Loading */}
        {ev === undefined && (
          <div style={{ textAlign: "center", color: "rgba(255,218,173,0.5)", padding: "3rem" }}>Loading…</div>
        )}

        {/* Not found */}
        {ev === null && (
          <div style={{
            textAlign: "center", padding: "3rem",
            border: "1px dashed rgba(252,179,31,0.35)", borderRadius: "14px",
            color: "rgba(255,218,173,0.7)",
          }}>
            <div style={{ fontFamily: "var(--jop-script)", fontSize: "1.8rem", color: "var(--jop-gold)", marginBottom: "0.5rem" }}>
              Event not found
            </div>
            <p style={{ fontSize: "0.9rem", lineHeight: 1.55 }}>
              This event may have ended or been taken down.{" "}
              <a href="/events" style={{ color: "var(--jop-pink)", fontWeight: 700, textDecoration: "none" }}>See all events →</a>
            </p>
          </div>
        )}

        {/* Event detail */}
        {ev && (
          <>
            <PanBar width={72} height={3} margin="0 0 1.2rem" />

            {/* Live badge */}
            {isLive && (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: "0.4rem",
                background: "rgba(112,190,68,0.15)", border: "1px solid rgba(112,190,68,0.5)",
                borderRadius: "999px", padding: "0.35rem 0.85rem", marginBottom: "1rem",
              }}>
                <span style={{
                  width: "8px", height: "8px", borderRadius: "50%", background: "#70be44",
                  boxShadow: "0 0 6px rgba(112,190,68,0.8)",
                  animation: "jopPulse 2s ease-in-out infinite",
                }} />
                <span style={{ color: "#70be44", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                  We're cooking right now
                </span>
              </div>
            )}

            {/* Status message */}
            {ev.publicStatusMessage && (
              <div style={{
                background: "rgba(243,156,18,0.1)", border: "1px solid rgba(243,156,18,0.4)",
                borderRadius: "10px", padding: "0.65rem 0.9rem", marginBottom: "1.25rem",
                color: "#f5c842", fontSize: "0.88rem", fontWeight: 600,
              }}>
                {ev.publicStatusMessage}
              </div>
            )}

            {/* Title */}
            <h1 style={{
              margin: "0 0 0.5rem",
              fontFamily: "var(--jop-script)",
              fontSize: "clamp(2rem, 5.5vw, 3rem)",
              color: "white", fontWeight: 700, lineHeight: 1.1,
            }}>
              {ev.publicTitle || ev.eventName || "Event"}
            </h1>

            {/* Date + time */}
            {ev.confirmedDateMs && (
              <div style={{ color: "var(--jop-gold)", fontSize: "1.1rem", fontWeight: 700, marginBottom: "0.3rem" }}>
                {formatDate(ev.confirmedDateMs)}
                {ev.confirmedStartMs && `, ${formatTime(ev.confirmedStartMs)}`}
                {ev.confirmedEndMs && ` – ${formatTime(ev.confirmedEndMs)}`}
              </div>
            )}

            {/* Location */}
            {(ev.publicLocation || ev.venueName) && (
              <div style={{ color: "rgba(255,218,173,0.65)", fontSize: "0.95rem", marginBottom: "1.25rem" }}>
                {ev.publicLocation || ev.venueName}
                {ev.venueAddress && ev.venueAddress !== ev.publicLocation && (
                  <span style={{ color: "rgba(255,218,173,0.4)" }}> · {ev.venueAddress}</span>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
              <button onClick={async () => { const fellBack = await shareEvent(ev); if (fellBack) { setCopied(true); setTimeout(() => setCopied(false), 2000); } }} style={{
                display: "inline-flex", alignItems: "center", gap: "0.35rem",
                background: "rgba(239,83,126,0.12)", border: "1px solid rgba(239,83,126,0.4)",
                color: "var(--jop-pink)", borderRadius: "999px",
                padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: 700,
                cursor: "pointer", fontFamily: "inherit",
              }}>
                {copied ? "Link copied!" : "🔗 Share"}
              </button>
              {gcal && (
                <a href={gcal} target="_blank" rel="noopener noreferrer" style={{
                  display: "inline-flex", alignItems: "center", gap: "0.35rem",
                  background: "rgba(252,179,31,0.12)", border: "1px solid rgba(252,179,31,0.4)",
                  color: "var(--jop-gold)", borderRadius: "999px",
                  padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: 700,
                  textDecoration: "none",
                }}>
                  📅 Add to calendar
                </a>
              )}
              {maps && (
                <a href={maps} target="_blank" rel="noopener noreferrer" style={{
                  display: "inline-flex", alignItems: "center", gap: "0.35rem",
                  background: "rgba(74,164,225,0.12)", border: "1px solid rgba(74,164,225,0.4)",
                  color: "var(--jop-highlight-blue)", borderRadius: "999px",
                  padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: 700,
                  textDecoration: "none",
                }}>
                  📍 Get directions
                </a>
              )}
              {ev.ticketingUrl && (
                <a href={ev.ticketingUrl} target="_blank" rel="noopener noreferrer" style={{
                  display: "inline-flex", alignItems: "center", gap: "0.35rem",
                  background: "rgba(239,83,126,0.12)", border: "1px solid rgba(239,83,126,0.4)",
                  color: "var(--jop-pink)", borderRadius: "999px",
                  padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: 700,
                  textDecoration: "none",
                }}>
                  🎟 Get tickets
                </a>
              )}
            </div>

            {/* Apple Calendar / Outlook fallback */}
            {gcal && (
              <div style={{ marginTop: "-1rem", marginBottom: "1.5rem" }}>
                <button onClick={() => downloadIcs(ev)} style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: "rgba(255,218,173,0.45)", fontSize: "0.78rem",
                  fontFamily: "inherit", textDecoration: "underline", padding: 0,
                }}>
                  Other calendars (.ics)
                </button>
              </div>
            )}

            {/* Producer-curated links — venue, headliner, event socials.
                Tickets already has its own button above, so skip dupes. */}
            {(ev.publicLinks || []).filter((l) => l.url && l.url !== ev.ticketingUrl).length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1.5rem" }}>
                {(ev.publicLinks || [])
                  .filter((l) => l.url && l.url !== ev.ticketingUrl)
                  .map((l, i) => (
                    <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" style={{
                      display: "inline-flex", alignItems: "center", gap: "0.35rem",
                      background: "rgba(255,218,173,0.07)", border: "1px solid rgba(255,218,173,0.25)",
                      color: "var(--jop-cream)", borderRadius: "999px",
                      padding: "0.45rem 0.95rem", fontSize: "0.82rem", fontWeight: 600,
                      textDecoration: "none",
                    }}>
                      {{ artist: "🎤", venue: "📍", social: "📣" }[l.type] || "🔗"} {l.label} ↗
                    </a>
                  ))}
              </div>
            )}

            {/* Description */}
            {ev.publicDescription && (
              <div style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,218,173,0.1)",
                borderRadius: "14px", padding: "1.25rem 1.2rem",
                marginBottom: "1.5rem",
              }}>
                <p style={{
                  margin: 0, color: "rgba(255,218,173,0.82)",
                  fontSize: "0.95rem", lineHeight: 1.7, whiteSpace: "pre-wrap",
                }}>
                  {ev.publicDescription}
                </p>
              </div>
            )}

            {/* Past event — pancake gallery */}
            {isPast && photos.length > 0 && (
              <section style={{ marginTop: "0.5rem" }}>
                <div style={{
                  color: "rgba(255,218,173,0.45)", fontSize: "0.72rem",
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  marginBottom: "0.85rem", fontWeight: 700,
                }}>
                  Pancakes from this event{count > 0 ? ` · ${count} total` : ""}
                </div>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                  gap: "0.75rem",
                }}>
                  {photos.map((url, i) => (
                    <div key={i} style={{
                      aspectRatio: "1 / 1", borderRadius: "12px",
                      overflow: "hidden", background: "rgba(0,0,0,0.3)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <img src={url} alt="" loading="lazy" style={{
                        maxWidth: "100%", maxHeight: "100%", objectFit: "contain",
                        filter: "drop-shadow(0 8px 20px rgba(0,0,0,0.5))",
                      }} />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Past event — stats callout when shift linked but no photos yet */}
            {isPast && count > 0 && photos.length === 0 && (
              <div style={{
                textAlign: "center", padding: "1.5rem",
                border: "1px dashed rgba(255,218,173,0.15)", borderRadius: "12px",
                color: "rgba(255,218,173,0.55)", fontSize: "0.88rem",
              }}>
                We made {count} pancake{count !== 1 ? "s" : ""} at this event.
                Photos will appear here once they're curated.
              </div>
            )}
          </>
        )}
      </main>

      <style>{`
        @keyframes jopPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      <SiteFooter />
    </div>
  );
}
